/**
 * File System Manager for Orthanc Duplication
 * Manages the orthanc_duplicata/ directory structure:
 * orthanc_duplicata/studies/{studyId}/series/{seriesId}/instances/{instanceId}/
 */

/**
 * The one place this key is spelled out, read and written through the three
 * accessors below and nowhere else.
 *
 * Module scope rather than a field of the singleton: what it records is a property of
 * the browser profile, not of a live handle, and it is the only part of the duplication
 * state readable synchronously at any point of the page — the in-memory handle starts
 * null on every reload, and its IndexedDB record only comes back through the async
 * restore, which needs the browser to have kept the grant.
 */
const ACCESS_STORAGE_KEY = 'orthanc_duplicata_access';

/**
 * The persisted duplication access, normalised so a hand-edited or truncated value
 * degrades to "never duplicated" instead of reaching the callers.
 * @returns {{hasDirectoryAccess: boolean, directoryName: string|null}}
 */
export const readPersistedAccess = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ACCESS_STORAGE_KEY));

    if (parsed && typeof parsed === 'object') {
      return {
        hasDirectoryAccess: parsed.hasDirectoryAccess === true,
        directoryName: parsed.directoryName || null,
      };
    }
  } catch {
    // Unparseable, or storage denied outright: neither is a duplication.
  }

  return { hasDirectoryAccess: false, directoryName: null };
};

/**
 * Whether a duplication was ever authorised on this profile.
 *
 * Synchronous, which is the point: the report cache has to know before it decides
 * whether it may look anywhere but the disk, and it decides during a read it must
 * not lengthen.
 *
 * True from the moment the directory is granted, so a run still in flight counts as
 * one — a report not yet written is then an absence, which is the same answer the
 * user will get once the run has ended without writing it.
 * @returns {boolean}
 */
export const hasPersistedDirectoryAccess = () =>
  readPersistedAccess().hasDirectoryAccess;

/**
 * Records the directory the user just granted. Deliberately unguarded: a profile
 * that cannot write this flag cannot be trusted to remember the duplication either,
 * so the failure belongs to the caller that reports the phase.
 * @param {string} directoryName
 */
export const writePersistedAccess = directoryName => {
  localStorage.setItem(
    ACCESS_STORAGE_KEY,
    JSON.stringify({ hasDirectoryAccess: true, directoryName })
  );
};

class OrthancFileSystemManager {
  constructor() {
    this.directoryHandle = null;
    this.isSupported = 'showDirectoryPicker' in window;
    this.rootDirName = 'orthanc_duplicata';
    this.dbName = 'orthanc_duplicata_fs';
    this.handleKey = 'orthanc_duplicata_handle';
    // Cache: path string → FileSystemDirectoryHandle
    this._dirCache = new Map();
  }

  /**
   * Check if File System Access API is supported
   * @returns {boolean}
   */
  isFileSystemAccessSupported() {
    return this.isSupported;
  }

  /**
   * Request directory access from user
   * @returns {Promise<boolean>} True if access granted
   */
  async requestAccess() {
    if (!this.isSupported) {
      throw new Error(
        'File System Access API not supported in this browser, please use Chrome, Edge or Opera'
      );
    }

    try {
      // Request directory picker
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads',
      });

      // Save parent folder name before overwriting the handle
      const parentName = this.directoryHandle.name;

      // Create root directory for orthanc duplication
      const rootDir = await this.directoryHandle.getDirectoryHandle(
        this.rootDirName,
        { create: true }
      );

      // Store the root directory handle
      this.directoryHandle = rootDir;

      // Persist handle in IndexedDB
      await this.storeDirectoryHandle();

      // Persist parent folder name across page reloads
      localStorage.setItem('orthanc_dir_parent_name', parentName);

      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[OrthancFS] Error requesting directory access:', error);
      }
      return false;
    }
  }

  /**
   * Store directory handle in IndexedDB
   */
  async storeDirectoryHandle() {
    if (!this.directoryHandle) {
      return;
    }

    try {
      const db = await this.openDB();
      const tx = db.transaction('directory_handles', 'readwrite');
      const store = tx.objectStore('directory_handles');

      await new Promise((resolve, reject) => {
        const request = store.put({
          id: this.handleKey,
          handle: this.directoryHandle,
          timestamp: Date.now(),
        });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('[OrthancFS] Error storing directory handle:', error);
    }
  }

  /**
   * Whether the handle already in memory is still readable, without ever prompting.
   *
   * queryPermission only reports the current state, unlike requestPermission which
   * opens a dialog under a user gesture and resolves to 'prompt' without one, so a
   * read path can call this one safely.
   * @returns {Promise<boolean>}
   */
  async hasLiveAccess() {
    if (!this.directoryHandle) {
      return false;
    }

    try {
      const permission = await this.directoryHandle.queryPermission({
        mode: 'readwrite',
      });
      return permission === 'granted';
    } catch {
      return false;
    }
  }

  /**
   * Restore directory handle from IndexedDB
   *
   * Keeps the live handle instead of rehydrating it: reading the record back gives a
   * structured clone, so a new object on every call. Consumers cache against that
   * object identity — offlineReportStore would reparse inventory.json, megabytes of
   * it, on every study opened from the cache.
   * @returns {Promise<boolean>}
   */
  async restoreDirectoryHandle() {
    if (await this.hasLiveAccess()) {
      return true;
    }

    try {
      const db = await this.openDB();
      const tx = db.transaction('directory_handles', 'readonly');
      const store = tx.objectStore('directory_handles');

      const record = await new Promise((resolve, reject) => {
        const request = store.get(this.handleKey);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (record && record.handle) {
        this.directoryHandle = record.handle;

        // Verify permission
        const permission = await this.directoryHandle.requestPermission({
          mode: 'readwrite',
        });

        if (permission !== 'granted') {
          this.directoryHandle = null;
          return false;
        }

        return true;
      }
    } catch (error) {
      console.error('[OrthancFS] Error restoring directory handle:', error);
    }

    return false;
  }

  /**
   * Open IndexedDB for storing directory handles
   */
  async openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('directory_handles')) {
          db.createObjectStore('directory_handles', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Ensure we have directory access
   * @returns {Promise<boolean>}
   */
  async ensureAccess() {
    if (this.directoryHandle) {
      return true;
    }

    const restored = await this.restoreDirectoryHandle();
    if (!restored) {
      throw new Error('No directory access. Call requestAccess() first.');
    }

    return true;
  }

  /**
   * Get a cached directory handle or create it, caching the result.
   * @param {string} cacheKey - Unique path string used as cache key
   * @param {FileSystemDirectoryHandle} parentHandle - Parent directory
   * @param {string} name - Directory name to get/create
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async _getCachedDir(cacheKey, parentHandle, name) {
    if (this._dirCache.has(cacheKey)) {
      return this._dirCache.get(cacheKey);
    }
    const handle = await parentHandle.getDirectoryHandle(name, {
      create: true,
    });
    this._dirCache.set(cacheKey, handle);
    return handle;
  }

  /**
   * Get or create study directory
   * @param {string} studyId - Study Orthanc ID
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getOrCreateStudyDirectory(studyId) {
    await this.ensureAccess();

    const studiesDir = await this._getCachedDir(
      'studies',
      this.directoryHandle,
      'studies'
    );
    return this._getCachedDir(`studies/${studyId}`, studiesDir, studyId);
  }

  /**
   * Get or create series directory
   * @param {string} studyId - Study Orthanc ID
   * @param {string} seriesId - Series Orthanc ID
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getOrCreateSeriesDirectory(studyId, seriesId) {
    const studyDir = await this.getOrCreateStudyDirectory(studyId);

    const seriesRootDir = await this._getCachedDir(
      `studies/${studyId}/series`,
      studyDir,
      'series'
    );
    return this._getCachedDir(
      `studies/${studyId}/series/${seriesId}`,
      seriesRootDir,
      seriesId
    );
  }

  /**
   * Get or create instance directory
   * @param {string} studyId - Study Orthanc ID
   * @param {string} seriesId - Series Orthanc ID
   * @param {string} instanceId - Instance Orthanc ID
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getOrCreateInstanceDirectory(studyId, seriesId, instanceId) {
    const seriesDir = await this.getOrCreateSeriesDirectory(studyId, seriesId);

    const instancesRootDir = await this._getCachedDir(
      `studies/${studyId}/series/${seriesId}/instances`,
      seriesDir,
      'instances'
    );
    return this._getCachedDir(
      `studies/${studyId}/series/${seriesId}/instances/${instanceId}`,
      instancesRootDir,
      instanceId
    );
  }

  /**
   * Get instance directory without creating it. Returns null if not found.
   * @param {string} studyId
   * @param {string} seriesId
   * @param {string} instanceId
   * @returns {Promise<FileSystemDirectoryHandle|null>}
   */
  async getInstanceDirectory(studyId, seriesId, instanceId) {
    if (!this.directoryHandle) {
      return null;
    }
    try {
      const studiesDir = await this.directoryHandle.getDirectoryHandle(
        'studies'
      );
      const studyDir = await studiesDir.getDirectoryHandle(studyId);
      const seriesRootDir = await studyDir.getDirectoryHandle('series');
      const seriesDir = await seriesRootDir.getDirectoryHandle(seriesId);
      const instancesRootDir = await seriesDir.getDirectoryHandle('instances');
      return await instancesRootDir.getDirectoryHandle(instanceId);
    } catch {
      return null;
    }
  }

  /**
   * Get study directory without creating it. Returns null if not found.
   *
   * Deliberately does not call restoreDirectoryHandle(): that one goes through
   * requestPermission, which outside a user gesture resolves to 'prompt' whenever the
   * grant is gone, and clears directoryHandle as a side effect. This one is called
   * over and over, deep inside thumbnail and DICOM reads, so a single unlucky call
   * would revoke the access all the others are relying on.
   *
   * Two callers do run the restore, and on purpose: loadStudyFromOrthancCache in
   * orthancCacheStudyLoader and loadCachedSeriesIDs below — both once per page,
   * before anything holds an access to lose. Their
   * hasLiveAccess() short-circuit spares them only while directoryHandle is already
   * non-null, which a reload never leaves it, so they are the ones paying the
   * 'prompt' risk — and the reason the offline mode survives a reload at all.
   * @param {string} studyId
   * @returns {Promise<FileSystemDirectoryHandle|null>}
   */
  async getStudyDirectory(studyId) {
    if (!this.directoryHandle) {
      return null;
    }
    try {
      const studiesDir = await this.directoryHandle.getDirectoryHandle(
        'studies'
      );
      return await studiesDir.getDirectoryHandle(studyId);
    } catch {
      return null;
    }
  }

  /**
   * Get series directory without creating it. Returns null if not found.
   *
   * Same precaution as getStudyDirectory, which it goes through: no create flag and
   * no restoreDirectoryHandle(), whose requestPermission would resolve to 'prompt'
   * outside a user gesture and clear directoryHandle as a side effect, revoking the
   * access the thumbnails and the DICOM loader are relying on.
   * @param {string} studyId
   * @param {string} seriesId
   * @returns {Promise<FileSystemDirectoryHandle|null>}
   */
  async getSeriesDirectory(studyId, seriesId) {
    const studyDir = await this.getStudyDirectory(studyId);
    if (!studyDir) {
      return null;
    }
    try {
      const seriesRootDir = await studyDir.getDirectoryHandle('series');
      return await seriesRootDir.getDirectoryHandle(seriesId);
    } catch {
      return null;
    }
  }

  /**
   * Save JSON file to directory
   * @param {FileSystemDirectoryHandle} directory - Target directory
   * @param {string} filename - File name (e.g., 'metadata.json')
   * @param {object} data - Data to save
   * @returns {Promise<boolean>}
   */
  async saveJSON(directory, filename, data) {
    try {
      const fileHandle = await directory.getFileHandle(filename, {
        create: true,
      });
      const writable = await fileHandle.createWritable();
      await writable.write(
        new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();
      return true;
    } catch (error) {
      console.error(`[OrthancFS] Error saving JSON ${filename}:`, error);
      return false;
    }
  }

  /**
   * Save binary file to directory
   * @param {FileSystemDirectoryHandle} directory - Target directory
   * @param {string} filename - File name (e.g., 'file.dcm')
   * @param {Blob} blob - Binary data
   * @returns {Promise<boolean>}
   */
  async saveBinary(directory, filename, blob) {
    try {
      const fileHandle = await directory.getFileHandle(filename, {
        create: true,
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      console.error(`[OrthancFS] Error saving binary ${filename}:`, error);
      return false;
    }
  }

  /**
   * Stream a fetch Response body directly to a file using pipeTo.
   *
   * No per-chunk callback: the progress counted by the duplication is slots, one per
   * file, so a byte tally had nothing left to feed and only bought a TransformStream
   * in the middle of every DICOM write.
   * @param {FileSystemDirectoryHandle} directory - Target directory
   * @param {string} filename - File name
   * @param {Response} response - Fetch Response whose body will be piped
   * @returns {Promise<void>}
   */
  async streamResponseToFile(directory, filename, response) {
    const fileHandle = await directory.getFileHandle(filename, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await response.body.pipeTo(writable);
  }

  /**
   * Get file from directory
   * @param {FileSystemDirectoryHandle} directory - Directory
   * @param {string} filename - File name
   * @returns {Promise<File|null>}
   */
  async getFile(directory, filename) {
    try {
      const fileHandle = await directory.getFileHandle(filename);
      return await fileHandle.getFile();
    } catch (error) {
      return null;
    }
  }

  /**
   * 'Scan studies/ * /series/ and return all series directory names (Orthanc series IDs).
   * Silently returns [] if no directory access is available.
   * @returns {Promise<string[]>}
   */
  async loadCachedSeriesIDs() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) return [];
    }
    const seriesIDs = [];
    let studiesDir;
    try {
      studiesDir = await this.directoryHandle.getDirectoryHandle('studies');
    } catch {
      return [];
    }
    for await (const [, studyDirHandle] of studiesDir) {
      if (studyDirHandle.kind !== 'directory') continue;
      let seriesRootDir;
      try {
        seriesRootDir = await studyDirHandle.getDirectoryHandle('series');
      } catch {
        continue;
      }
      for await (const [seriesId, seriesDirHandle] of seriesRootDir) {
        if (seriesDirHandle.kind === 'directory') {
          seriesIDs.push(seriesId);
        }
      }
    }
    return seriesIDs;
  }

  /**
   * Clear current directory access (forces re-prompt on next requestAccess)
   */
  clearAccess() {
    this.directoryHandle = null;
    this._dirCache.clear();
  }

  /**
   * Check if a previous download exists (inventory.json present)
   * @returns {Promise<boolean>}
   */
  async hasExistingData() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) return false;
    }
    try {
      await this.directoryHandle.getFileHandle('inventory.json');
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
const orthancFileSystemManager = new OrthancFileSystemManager();

export default orthancFileSystemManager;
